import React, { FC, useState } from 'react';

import { IdentityTypeEnum } from '@/gql/types.generated';
//eslint-disable-next-line no-restricted-imports
import { useHistory, Link } from 'react-router-dom';
import { Form, Input, Button } from 'antd';
import { useSignupRequest } from './singin.generated';
import { usePrefetchPartnerData } from '@/shared/access';
import { LocalStorage } from '@/core/Storage';
import { LoginLayout } from '@pages/Login/LoginLayout';
import styled from 'styled-components';
import { gettext, useGettext } from '@cranium/i18n';
import { PasswordInput } from 'antd-password-input-strength';
import { emailValidator } from '@/libs/validators';
import { Title } from '@pages/Login/component';

function requiredValidator(type: 'email' | 'phone') {
  return (rule: any, value: string, callBack: (message?: any) => void) => {
    callBack(!value ? (type === 'email' ? gettext('Email is incorrect') : gettext('Phone is incorrect')) : undefined);
  };
}

export const SignUp: FC = () => {
  const prefetchPartnerData = usePrefetchPartnerData();
  const signup = useSignupRequest();
  const history = useHistory();
  const [level, setLevel] = useState(0);
  const [form] = Form.useForm();
  const { gettext } = useGettext();

  const handleSubmit = () => {
    form.validateFields().then(values => {
      let partnerId: string | null | undefined;
      return signup({
        email: { email: values.email, password: values.password }
      }).then(resp => {
        partnerId = resp?.data?.authSignUp?.partnerId;
        if (partnerId) {
          return prefetchPartnerData(partnerId, IdentityTypeEnum.Contractor).then(() => {
            LocalStorage.removeItem('location.beforeLogin');
            history.push(`/p-${partnerId}/`);
          });
        }
      });
    });
  };

  return (
    <LoginLayout>
      <Main>
        <Header>
          <Title>{gettext('Create your account')}</Title>
          <Link to={`/login/`}>{gettext('Back to login')}</Link>
        </Header>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label={gettext('Email')}
            name="email"
            rules={[{ validator: emailValidator }, { validator: requiredValidator('email') }]}
          >
            <Input type="email" placeholder={gettext('Email')} />
          </Form.Item>
          <Form.Item
            label={gettext('Password')}
            name="password"
            rules={[
              { required: true, message: gettext('Required') },
              {
                validator: async () => {
                  return level >= 1 ? Promise.resolve() : Promise.reject(gettext('Password is too weak'));
                },
                message: gettext('Password is too weak')
              }
            ]}
          >
            <PasswordInput onLevelChange={setLevel} />
          </Form.Item>
          <Form.Item
            label={gettext('Confirm password')}
            name={'confirm_password'}
            dependencies={['password']}
            rules={[
              {
                required: true,
                message: 'Please confirm your password!'
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(gettext('The two passwords that you entered do not match!'));
                }
              })
            ]}
          >
            <Input.Password placeholder={gettext('Confirm password')} />
          </Form.Item>
          <Fotter>
            <Info>
              <p>{gettext('Your password should contain:')}</p>
              <ul>
                <li>{gettext('At least one lowercase letter')}</li>
                <li>{gettext('At least one number')}</li>
                <li>{gettext('A Minimum of 8 characters')}</li>
              </ul>
            </Info>
            <Button type="primary" onClick={() => handleSubmit()}>
              {gettext('Create account')}
            </Button>
          </Fotter>
        </Form>
      </Main>
    </LoginLayout>
  );
};

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  > h1 {
    margin-bottom: 0;
  }
  padding-bottom: 12px;
`;
const Fotter = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;
const Info = styled.div`
  font: var(--font-regular-14);
  color: var(--gray-7);
  p {
    margin-bottom: 4px;
  }
  ul {
    padding-left: 20px;
  }
`;
const Main = styled.div`
  width: 420px;
  max-width: 100%;
  a {
    display: flex;
    align-items: center;
    gap: 4px;
    &:hover {
      color: var(--green-7);
      text-decoration: none;
    }
  }

  > form {
    display: flex;
    flex-direction: column;
  }
`;
